/**
 * Shared condition evaluation logic for the dynamic form engine (v2).
 *
 * Used by:
 *   - frontend: ConditionEngine.ts (re-exports from here)
 *   - backend:  formValidation.ts (imported via @livingsyncs/types)
 *
 * Single implementation — no divergence between server-side validation and
 * the client-side renderer's visibility/required logic.
 */

import type { Condition, SimpleCondition, CompoundCondition } from "./forms";

type FormValues = Record<string, unknown>;

function isCompound(c: Condition): c is CompoundCondition {
  return "rules" in c;
}

function evaluateSimple(
  condition: SimpleCondition,
  values: FormValues,
): boolean {
  const raw = values[condition.field];

  switch (condition.operator) {
    case "isEmpty":
      return raw === undefined || raw === null || raw === "" || raw === false;
    case "isNotEmpty":
      return raw !== undefined && raw !== null && raw !== "" && raw !== false;
    case "equals":
      // Loose equality intentional: "true" == true for checkbox fields
      return raw == condition.value; // NOSONAR
    case "notEquals":
      return raw != condition.value; // NOSONAR
    case "contains": {
      const s = typeof raw === "string" ? raw : String(raw ?? "");
      return s
        .toLowerCase()
        .includes(String(condition.value ?? "").toLowerCase());
    }
    case "greaterThan":
      return Number(raw) > Number(condition.value);
    case "lessThan":
      return Number(raw) < Number(condition.value);
    default:
      return true;
  }
}

/** Evaluates a Condition against the current form values. Returns true = condition met. */
export function evaluateCondition(
  condition: Condition | undefined,
  values: FormValues,
): boolean {
  if (!condition) return true;

  if (isCompound(condition)) {
    const results = condition.rules.map((r) => evaluateCondition(r, values));
    return condition.operator === "AND"
      ? results.every(Boolean)
      : results.some(Boolean);
  }

  return evaluateSimple(condition, values);
}

/** Returns true if the field should be visible given current values. */
export function isFieldVisible(
  visibleIf: Condition | undefined,
  values: FormValues,
): boolean {
  return evaluateCondition(visibleIf, values);
}

/** Returns true if the field is required given current values. */
export function isFieldRequired(
  staticRequired: boolean | undefined,
  requiredIf: Condition | undefined,
  values: FormValues,
): boolean {
  if (staticRequired) return true;
  if (requiredIf) return evaluateCondition(requiredIf, values);
  return false;
}
